import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const SECTIONS = [
  {
    n: "01",
    ar: "ٱلْبِدَايَة",
    title: "Préparer son parcours",
    intro: "Avant d'ouvrir le guide, mets en place les conditions du succès.",
    steps: [
      "Fixe un créneau quotidien de 15 à 25 minutes — idéalement après la prière de Fajr, l'esprit clair.",
      "Choisis ton support : le guide PDF imprimé, ou ouvert sur tablette/téléphone.",
      "Définis ton intention (niyya) et un objectif réaliste inscrit en première page du guide.",
    ],
  },
  {
    n: "02",
    ar: "ٱلْحِفْظ",
    title: "Mémoriser — le plan de 6 mois",
    intro: "Chaque journée du guide suit le même rituel simple.",
    steps: [
      "Lis la section du jour assignée par le planning, en comprenant le sens général.",
      "Écoute la récitation des versets (2 à 3 fois) avant de les répéter toi-même.",
      "Répète verset par verset, puis enchaîne sans regarder — c'est l'ancrage.",
      "Coche la journée dans le tableau de suivi : voir la progression nourrit la motivation.",
      "Rythme hebdomadaire : 5 jours de mémorisation, 1 jour de consolidation, 1 jour de repos actif (révision libre).",
    ],
  },
  {
    n: "03",
    ar: "ٱلْمُرَاجَعَة",
    title: "Réviser — le plan de 30 jours",
    intro: "Un cycle quotidien pour consolider ce qui est déjà mémorisé.",
    steps: [
      "Le guide divise ta révision en 30 blocs quotidiens — un bloc par jour, sans exception.",
      "Matin : récite le bloc du jour à voix haute, sans support si possible.",
      "Soir : auto-test avec la grille du guide — note les passages fragiles.",
      "Les passages fragiles reviennent automatiquement dans le cycle des jours suivants (répétition espacée).",
    ],
  },
  {
    n: "04",
    ar: "ٱلْمُتَابَعَة",
    title: "Suivre sa progression",
    intro: "Le suivi est le cœur de la méthode : il transforme l'effort en preuve visible.",
    steps: [
      "Remplis le tableau de suivi chaque jour : jours complétés, pages apprises, pages restantes.",
      "Règle d'or : ne saute jamais deux jours de suite. Un jour manqué se rattrape, deux créent une rupture.",
      "Chaque semaine, relis ton tableau : ajuste le rythme si nécessaire, sans culpabiliser.",
    ],
  },
];

const RULES = [
  { n: "1", title: "La régularité bat la quantité", text: "Mieux vaut 3 versets chaque jour que 3 pages une fois par semaine." },
  { n: "2", title: "Réviser avant d'ajouter", text: "Aucune nouvelle mémorisation sans réviser d'abord la veille." },
  { n: "3", title: "Réciter à quelqu'un", text: "Une fois par semaine, récite à un proche, un professeur ou un ami." },
  { n: "4", title: "Accepter les plateaux", text: "Les périodes difficiles font partie du parcours — le plan t'aide à les traverser." },
];

export default function MethodePage() {
  return (
    <div className="info-page" data-testid="methode-page">
      <header className="info-header">
        <Link to="/" className="info-back" data-testid="back-home-link">
          <ArrowLeft size={16} />
          Retour à l'accueil
        </Link>
        <Link to="/" className="logo logo-link" data-testid="brand-home-link" aria-label="SamNara — accueil"><img src="/logo-samnara.png" alt="SamNara" className="logo-img logo-img-lg" /></Link>
      </header>

      <div className="container">
        <motion.div
          className="info-hero"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE }}
        >
          <div className="eyebrow">Mode d'emploi</div>
          <h1>Comment utiliser les guides</h1>
          <p className="guides-lede">
            Une méthode complète, pas à pas : préparer, mémoriser, réviser et suivre —
            pour avancer chaque jour sans se sentir dépassé.
          </p>
        </motion.div>

        <motion.div
          className="method-video"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, ease: EASE }}
          data-testid="method-video-section"
        >
          <div className="method-video-frame">
            <video
              controls
              preload="metadata"
              playsInline
              poster="/videos/methode-poster.jpg"
              src="/videos/methode.mp4"
              data-testid="method-video-player"
            />
          </div>
          <div className="method-video-caption">
            <span className="chapter-ar" aria-hidden="true">شَاهِد</span>
            <p>La méthode expliquée en vidéo — quelques minutes pour tout comprendre avant de commencer.</p>
          </div>
        </motion.div>

        <div className="chapter-list method-list">
          {SECTIONS.map((s, i) => (
            <motion.article
              className="chapter"
              key={s.n}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, delay: 0.06, ease: EASE }}
              data-testid={`method-section-${s.n}`}
            >
              <div className="chapter-num" aria-hidden="true">{s.n}</div>
              <div className="chapter-body">
                <div className="chapter-ar">{s.ar}</div>
                <h3>{s.title}</h3>
                <p className="method-intro">{s.intro}</p>
                <ul className="m-steps">
                  {s.steps.map((st, j) => (
                    <li key={j}>
                      <span className="step-num">{j + 1}</span>
                      {st}
                    </li>
                  ))}
                </ul>
              </div>
              <motion.div
                className="chapter-line"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.2, delay: 0.15, ease: EASE }}
                aria-hidden="true"
              />
            </motion.article>
          ))}
        </div>

        <motion.div
          className="rules-block"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE }}
          data-testid="golden-rules"
        >
          <div className="eyebrow">À retenir</div>
          <h2 className="rules-title">Les 4 règles d'or</h2>
          <div className="rules-grid">
            {RULES.map((r) => (
              <div className="rule-card" key={r.n} data-testid={`rule-${r.n}`}>
                <div className="rule-num">{r.n}</div>
                <b>{r.title}</b>
                <p>{r.text}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="method-cta"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: EASE }}
        >
          <h2>Prêt à commencer ?</h2>
          <p>Choisis ton guide et applique la méthode dès aujourd'hui.</p>
          <Link to="/guides" className="btn" data-testid="methode-cta-guides">
            Découvrir les guides
          </Link>
        </motion.div>
      </div>

      <footer className="info-footer">
        <div className="copyright">
          © SamNara — Guides numériques, livraison instantanée après achat
        </div>
      </footer>
    </div>
  );
}
